'use client'

import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope, faPhone, faXmark } from '@fortawesome/pro-solid-svg-icons'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Spinner,
  TextArea
} from '@teqplay/ui'
import { useTranslations } from 'next-intl'

import InputTag from '../basic/InputTag'
import { useMutationShareVessel } from '../../hooks/teqplay-api/useShareVessel'
import VesselDetailHeader from '../VesselDetails/VesselDetailHeader'
import { openConfirmModal } from '../basic/confirmModal'

import { useToast } from '@/app/components/teqplay-ui'
import { SHARE_MEDIUM, TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'
import { useAnalytics } from '@/app/lib/analytics'
import { TLineUp } from '@/api/v1/resources/lineup'
import { useMutationShareLineup } from '@/app/hooks/teqplay-api/useLineup'

import './ModalSharingVesselForm.scss'

// Props for vessel sharing mode
type TVesselSharingProps = {
  vessel: TVesselJourneyResponse
  lineup?: never
}

// Props for lineup sharing mode
type TLineupSharingProps = {
  vessel?: never
  lineup: TLineUp
}

export type TPropsModalSharingVesselForm = {
  open: boolean
  setOpen: (open: boolean) => void
  onSharedSuccess?: () => void
  disablePhones?: boolean
} & (TVesselSharingProps | TLineupSharingProps)

const ModalSharingVesselForm = ({
  vessel,
  lineup,
  open,
  setOpen: setParentOpen,
  onSharedSuccess,
  disablePhones = false
}: TPropsModalSharingVesselForm) => {
  const t = useTranslations()
  const { trackVesselShared, trackLineupShared } = useAnalytics()
  const { toast } = useToast()
  const [emails, setEmails] = useState<string[]>([])
  const [phones, setPhones] = useState<string[]>([])
  const [note, setNote] = useState<string>('')

  // Determine if we're in lineup sharing mode
  const isLineupMode = !!lineup

  // Vessel sharing mutation
  const { mutate: shareVessel, isPending: isPendingShareVessel } = useMutationShareVessel({
    onSuccess: () => {
      onSharedSuccess?.()
      clearForm()
      setParentOpen(false)
    },
    onError: () => {
      toast({
        title: t('ShareVessel.error'),
        variant: 'error'
      })
      setParentOpen(false)
    }
  })

  // Lineup sharing mutation
  const { mutate: shareLineup, isPending: isPendingShareLineup } = useMutationShareLineup({
    onSuccess: () => {
      toast({
        title: t('lineupPage.modal.shareSuccess'),
        variant: 'success'
      })
      onSharedSuccess?.()
      clearForm()
      setParentOpen(false)
    },
    onError: () => {
      toast({
        title: t('lineupPage.modal.shareError'),
        variant: 'error'
      })
      setParentOpen(false)
    }
  })

  const isPendingShare = isPendingShareVessel || isPendingShareLineup

  const setOpen = (open: boolean) => {
    setParentOpen(open)
    // if emails or phones is not empty, confirm before closing
    if ((emails.length > 0 || phones.length > 0) && !open) {
      openConfirmModal({
        title: t('Modal.confirmDiscardChangesTitle'),
        description: t('Modal.confirmDiscardChangesDescription'),
        confirmLabel: t('Modal.confirmDiscardChangesConfirmLabel'),
        cancelLabel: t('common.cancel'),
        onConfirm: () => {
          clearForm()
          setParentOpen(false)
        },
        onCancel: () => setParentOpen(true)
      })
    }
  }

  const onShare = () => {
    const recipients = [
      ...emails.map((email) => ({ recipient: email, medium: SHARE_MEDIUM.EMAIL })),
      ...phones.map((phone) => ({ recipient: phone, medium: SHARE_MEDIUM.WHATSAPP }))
    ]

    if (isLineupMode) {
      // Share lineup
      shareLineup(
        {
          id: lineup._id,
          recipients
        },
        {
          onSuccess: () => {
            // Track analytics - determine primary share method
            const hasEmails = emails.length > 0
            const shareMethod: 'EMAIL' | 'WHATSAPP' = hasEmails ? 'EMAIL' : 'WHATSAPP'

            trackLineupShared({
              lineupId: lineup._id,
              lineupName: lineup.name,
              shareMethod,
              recipientCount: recipients.length
            })
          }
        }
      )
    } else if (vessel) {
      // Share vessel
      shareVessel({
        imo: vessel.ship.imo,
        portId: vessel.arrival?.port?.id || '',
        recipients,
        note
      })

      // Track analytics - determine primary share method
      const hasEmails = emails.length > 0
      const shareMethod: 'EMAIL' | 'WHATSAPP' = hasEmails ? 'EMAIL' : 'WHATSAPP'

      trackVesselShared({
        imo: vessel.ship.imo,
        vesselName: vessel.ship.name || 'Unknown',
        shareMethod,
        recipientCount: recipients.length,
        portName: vessel.arrival?.port?.name || 'Unknown'
      })
    }
  }

  const clearForm = () => {
    setEmails([])
    setPhones([])
    setNote('')
  }

  // Determine modal title based on mode
  const modalTitle = isLineupMode ? t('lineupPage.modal.shareTitle') : t('ShareVessel.title')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogOverlay className="share-overlay" />
      <DialogContent aria-describedby="confirm-modal-description" className="dialog-share-vessel">
        <DialogClose asChild>
          <Button variant="neutral-tertiary" className="dialog-close-btn">
            <FontAwesomeIcon icon={faXmark} />
          </Button>
        </DialogClose>
        <DialogHeader className="dialog-share-vessel-header">
          <DialogTitle asChild>
            <h2>{modalTitle}</h2>
          </DialogTitle>
          <DialogDescription>
            {isLineupMode ? (
              <div className="lineup-share-description">
                <span className="lineup-name">{lineup.name}</span>
                <span className="lineup-vessel-count">
                  {t('lineupPage.modal.vesselCount', { count: lineup.vessels.length })}
                </span>
              </div>
            ) : (
              vessel && <VesselDetailHeader vessel={vessel} />
            )}
          </DialogDescription>
          <InputTag
            title={t('ShareVessel.shareViaEmail')}
            subtitle={t('ShareVessel.sendTo')}
            value={emails}
            onChange={setEmails}
            icon={<FontAwesomeIcon icon={faEnvelope} />}
            validationType="email"
          />
          {!disablePhones && (
            <InputTag
              title={t('ShareVessel.shareViaWhatsApp')}
              subtitle={t('ShareVessel.sendTo')}
              value={phones}
              onChange={(phones) => {
                // Sanitize incoming phones and also deduplicate based on sanitized values
                const sanitized = phones.map((phone) => phone.replace(/\s+/g, ''))
                const unique = sanitized.filter(
                  (phone, index, self) => self.indexOf(phone) === index
                )
                setPhones(unique)
              }}
              icon={<FontAwesomeIcon icon={faPhone} />}
              validationType="phone"
            />
          )}
        </DialogHeader>
        {(emails.length > 0 || (!disablePhones && phones.length > 0)) && (
          <>
            {!isLineupMode && (
              <TextArea
                placeholder={t('ShareVessel.addMessage')}
                className="dialog-share-vessel-note"
                onChange={(e) => setNote(e.target.value)}
                value={note}
              />
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="brand-secondary" type="button">
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button
                variant="brand-primary"
                type="button"
                onClick={onShare}
                disabled={isPendingShare}
              >
                <span>{t('common.share')}</span>
                {isPendingShare && <Spinner size="s" loading />}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ModalSharingVesselForm
